'use client';

import { useMemo, useRef, useState } from 'react';
import { FEATURED_SYMBOL, getSymbolGroups, type SymbolSetKey } from '@/lib/symbol-data';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserSymbolPicker({
  setKey,
  usageSlug,
}: {
  setKey: SymbolSetKey;
  usageSlug: string;
}) {
  const usageReported = useRef(false);
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>([]);

  const featured = FEATURED_SYMBOL[setKey];

  const groups = useMemo(() => {
    const all = getSymbolGroups(setKey);
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all
      .map((group) => ({
        ...group,
        items: group.label.toLowerCase().includes(needle)
          ? group.items
          : group.items.filter(
              (item) =>
                item.name.toLowerCase().includes(needle) ||
                item.code.toLowerCase().includes(needle) ||
                item.char === query.trim(),
            ),
      }))
      .filter((group) => group.items.length > 0);
  }, [setKey, query]);

  async function copy(char: string) {
    try {
      await navigator.clipboard.writeText(char);
      setCopied(char);
      setRecent((past) => [char, ...past.filter((item) => item !== char)].slice(0, 10));
      setTimeout(() => setCopied((current) => (current === char ? null : current)), 1200);
      if (!usageReported.current) {
        usageReported.current = true;
        void consumeToolUsage(usageSlug, '').catch(() => undefined);
      }
    } catch {
      setCopied(null);
    }
  }

  const chip = (char: string, name: string, code: string) => (
    <button
      key={`${code}-${char}`}
      className="symbol-chip"
      onClick={() => void copy(char)}
      style={{
        background: copied === char ? 'var(--accent, #2a78d6)' : 'var(--surface-2, #1c2230)',
        border: '1px solid var(--border, #3a4152)',
        borderRadius: 8,
        color: '#fff',
        cursor: 'pointer',
        fontSize: '1.25rem',
        minWidth: 52,
        padding: '0.45rem 0.6rem',
      }}
      title={`${name} (U+${code})`}
      type="button"
    >
      {copied === char ? '✓' : char}
    </button>
  );

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{featured ? `Copy the ${featured.name.toLowerCase()}` : 'Copy any symbol'}</h2>
          <p>
            Tap a symbol to copy it. These are plain Unicode characters, so they paste into
            documents, chats, spreadsheets, and bios without an image.
          </p>
        </div>
        <span className="tool-live-badge">{featured ? featured.char : '★'}</span>
      </div>

      <div className="tool-live-uploader" style={{ gap: '0.9rem' }}>
        {featured ? (
          <button
            onClick={() => void copy(featured.char)}
            style={{
              background: 'transparent',
              border: '1px dashed var(--border, #3a4152)',
              borderRadius: 12,
              color: 'inherit',
              cursor: 'pointer',
              padding: '1.1rem 2rem',
              width: '100%',
            }}
            type="button"
          >
            <span style={{ display: 'block', fontSize: '4.5rem', lineHeight: 1.1 }}>
              {featured.char}
            </span>
            <small style={{ opacity: 0.75 }}>
              {copied === featured.char
                ? 'Copied to your clipboard'
                : `Tap to copy · ${featured.name} · U+${featured.code}`}
            </small>
          </button>
        ) : null}

        <label style={{ width: '100%' }}>
          Search by name or codepoint
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="heart, arrow, 2764…"
            type="text"
            value={query}
          />
        </label>

        {recent.length ? (
          <div style={{ width: '100%' }}>
            <small style={{ opacity: 0.7 }}>Recently copied</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 4 }}>
              {recent.map((char) => chip(char, 'Recently copied', `r${char.codePointAt(0)}`))}
            </div>
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.label} style={{ width: '100%' }}>
            <small style={{ opacity: 0.7 }}>{group.label}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 4 }}>
              {group.items.map((item) => chip(item.char, item.name, item.code))}
            </div>
          </div>
        ))}

        {!groups.length ? (
          <p className="tool-live-panel-note">
            Nothing matches that search. Try a name like arrow, a word like heart, or a codepoint
            like 2764.
          </p>
        ) : null}

        <p className="tool-live-panel-note">
          Every symbol here is a real character, not a picture. If one shows as a box on another
          device, that device is missing the font for it, and a plainer alternative from the same
          group will usually render everywhere.
        </p>
      </div>
    </article>
  );
}
